import React, { useState, useContext, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "./exclusionModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import { AlertTriangle, ArrowRight, CalendarCheck2, CalendarDays, Trash2 } from "lucide-react";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import translations from "../../../translations/translations";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import BannerMessageFixed from "../../common/bannerMessageFixed/bannerMessageFixed";
import StaffDropdown from "../../common/StaffDropdown/StaffDropdown";
import useModalDismiss from "../../common/useModalDismiss";

const ExclusionModal = ({
	onClose,
	onSaved,
	staffList = [],
	initialStaffId,
	token,
	scheduleData = [],
	onManageBooking,
	onStaffChange,
	initialTab = "add",
	prefillDate,
	prefillStart,
	prefillEnd,
}) => {
	useModalDismiss(onClose);
	const { userData, language } = useContext(CombinedContext);

	const [selectedStaffId, setSelectedStaffId] = useState(null);
	const [selectedScheduleId, setSelectedScheduleId] = useState(null);

	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [fullDay, setFullDay] = useState(true);
	const [startTime, setStartTime] = useState("09:00");
	const [endTime, setEndTime] = useState("17:00");
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);
	const [showStartCalendar, setShowStartCalendar] = useState(false);
	const [showEndCalendar, setShowEndCalendar] = useState(false);
	const [bookingConflicts, setBookingConflicts] = useState([]);
	const [banner, setBanner] = useState({ message: "", type: "error" });
	const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(initialTab);
	const [pendingDeletes, setPendingDeletes] = useState(new Set());
	const [addDirty, setAddDirty] = useState(false);

	const hasInitialized = useRef(false);

	// Switching staff starts a clean sheet: pending deletes are indices into the
	// previous staff's list, and a half-filled add form isn't theirs either.
	const selectStaff = (staff) => {
		setSelectedStaffId(staff.staffId);
		setSelectedScheduleId(staff.scheduleId);
		setPendingDeletes(new Set());
		setBookingConflicts([]);
		setAddDirty(false);
		setStartDate(null);
		setEndDate(null);
		setFullDay(true);
		setStartTime("09:00");
		setEndTime("17:00");
		setReason("");
		// let the calendar remember who we're on, so reopening lands here too
		if (onStaffChange) onStaffChange(staff.staffId);
	};

	// Only picks a staff member the first time — after that the selection belongs
	// to the user, so a save / week reload never drags them back to the default.
	useEffect(() => {
		if (staffList.length > 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			const target = initialStaffId ? staffList.find((s) => s.staffId === initialStaffId) : staffList[0];
			selectStaff(target || staffList[0]);
		}
	}, [staffList]);

	// Read straight from the latest schedule data instead of mirroring it into
	// state — a background week reload then can't clobber work in progress.
	const scheduleEntry = scheduleData.find((s) => s.staffId === selectedStaffId) || null;
	const exclusions = scheduleEntry?.exclusions || [];
	const bookingsByDate = scheduleEntry?.bookingsByDate || {};

	/* The list is trimmed for display here rather than on the server, because
	   deleteExclusion removes by position in the stored array — an index the
	   server can only get from us. Filtering server-side made every index the
	   client held point at the wrong entry, so deleting a block quietly removed
	   a different one. `index` is that stored position and is what travels back
	   on delete; the array order is presentation only.

	   "Past" is judged in the browser's clock, which is the shop's — the server
	   was comparing against the Lambda's UTC, four hours ahead of Santo Domingo,
	   so a block made after 8pm vanished from this list on the same evening the
	   calendar was still drawing it. */
	const visibleExclusions = exclusions
		.map((ex, index) => ({ ex, index }))
		.filter(({ ex }) => !dayjs(ex.endDate).endOf("day").isBefore(dayjs().startOf("day")));

	useEffect(() => {
		function handleClick(e) {
			if (!e.target.closest(".sf-calendar-popover") && !e.target.closest(".sf-date-trigger")) {
				setShowStartCalendar(false);
				setShowEndCalendar(false);
			}
		}

		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	/* Identity for an exclusion, since the records carry no id. Exact
	   duplicates would be indistinguishable, but doesOverlap() refuses to add
	   one that overlaps an existing entry, so two identical rows can't be
	   created through this modal. */
	const signature = (ex) => JSON.stringify([ex?.startDate, ex?.endDate, ex?.reason || "", ex?.blocks || []]);

	const showBanner = (message, type = "error") => {
		setBanner({ message, type });
	};

	const clearBanner = () => setBanner({ message: "", type: "error" });

	function getBlockedRangesForDate(dateStr) {
		return exclusions
			.filter((ex) => {
				const dateObj = dayjs(dateStr);
				const exStart = dayjs(ex.startDate);
				const exEnd = dayjs(ex.endDate);

				return !dateObj.isBefore(exStart) && !dateObj.isAfter(exEnd);
			})
			.flatMap((ex) => {
				if (!ex.blocks || ex.blocks.length === 0) {
					return [{ start: "00:00", end: "23:59" }];
				}
				return ex.blocks;
			});
	}

	function timeConflicts(dateStr, start, end) {
		if (!start || !end) return false;

		const blockedRanges = getBlockedRangesForDate(dateStr);

		return blockedRanges.some((b) => {
			return start < b.end && b.start < end;
		});
	}

	const isSingleDay = startDate === endDate;

	const times = Array.from({ length: 48 }, (_, i) => {
		const h = Math.floor(i / 2);
		const m = i % 2 === 0 ? "00" : "30";
		return `${String(h).padStart(2, "0")}:${m}`;
	});

	function isPast(date) {
		return dayjs(date).isBefore(dayjs(), "day");
	}

	function isFullyBlocked(date) {
		return exclusions.some((ex) => {
			const d = dayjs(date);
			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			const inRange = !d.isBefore(exStart) && !d.isAfter(exEnd);

			const isFull = !ex.blocks || ex.blocks.length === 0 || (ex.blocks[0]?.start === "00:00" && ex.blocks[0]?.end === "23:59");

			return inRange && isFull;
		});
	}

	function isPartiallyBlocked(date) {
		return exclusions.some((ex) => {
			const d = dayjs(date);
			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			const inRange = !d.isBefore(exStart) && !d.isAfter(exEnd);

			if (!inRange) return false;

			// Has blocks but not full-day
			return ex.blocks && ex.blocks.length > 0 && !(ex.blocks.length === 1 && ex.blocks[0].start === "00:00" && ex.blocks[0].end === "23:59");
		});
	}

	function doesOverlap(newExclusion) {
		return exclusions.some((ex) => {
			const newStart = dayjs(newExclusion.startDate);
			const newEnd = dayjs(newExclusion.endDate);

			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			// 1️⃣ Date range overlap
			const datesOverlap = !newEnd.isBefore(exStart) && !newStart.isAfter(exEnd);

			if (!datesOverlap) return false;

			const newIsFull = newExclusion.blocks.length === 0;
			const exIsFull = ex.blocks?.length === 0;

			// 2️⃣ If either is full day → overlap
			if (newIsFull || exIsFull) return true;

			// 3️⃣ Check time block overlap
			return newExclusion.blocks.some((nb) =>
				ex.blocks.some((eb) => {
					const newStartTime = dayjs(nb.start, "HH:mm");
					const newEndTime = dayjs(nb.end, "HH:mm");
					const exStartTime = dayjs(eb.start, "HH:mm");
					const exEndTime = dayjs(eb.end, "HH:mm");

					return newStartTime.isBefore(exEndTime) && exStartTime.isBefore(newEndTime);
				}),
			);
		});
	}

	function getBookingsForDate(dateStr) {
		return bookingsByDate?.[dateStr] || [];
	}

	function rangeOverlaps(aStart, aEnd, bStart, bEnd) {
		// assumes "HH:mm" for time strings
		return aStart < bEnd && bStart < aEnd;
	}

	function collectBookingConflicts(newExclusion) {
		const conflicts = [];

		const startD = dayjs(newExclusion.startDate);
		const endD = dayjs(newExclusion.endDate);

		// Walk each day in the date range
		for (let d = startD; !d.isAfter(endD, "day"); d = d.add(1, "day")) {
			const dateStr = d.format("YYYY-MM-DD");
			const bookings = getBookingsForDate(dateStr);

			if (!bookings.length) continue;

			// full day exclusion conflicts with ANY booking that day
			const isFullDay = !newExclusion.blocks || newExclusion.blocks.length === 0;

			if (isFullDay) {
				bookings.forEach((b) => conflicts.push(b));
				continue;
			}

			// partial day (your UI only allows partial for single day)
			const block = newExclusion.blocks[0];
			if (!block?.start || !block?.end) continue;

			// booking start/end are ISO, convert to HH:mm for comparison
			bookings.forEach((b) => {
				const bStart = dayjs(b.start).format("HH:mm");
				const bEnd = dayjs(b.end).format("HH:mm");

				if (rangeOverlaps(block.start, block.end, bStart, bEnd)) {
					conflicts.push(b);
				}
			});
		}

		// de-dupe by bookingId just in case
		const seen = new Set();
		return conflicts.filter((c) => {
			if (!c.bookingId) return true;
			if (seen.has(c.bookingId)) return false;
			seen.add(c.bookingId);
			return true;
		});
	}

	async function handleSave() {
		if (!selectedScheduleId) return;
		const t = translations[language].calendar.exclusion;

		const hasAdd = addDirty;
		const hasDeletes = pendingDeletes.size > 0;

		if (!hasAdd && !hasDeletes) {
			return; // nothing pending
		}

		// validate add only if it's pending
		let newExclusion = null;
		if (hasAdd) {
			if (!fullDay) {
				if (!startTime || !endTime) {
					showBanner(language === "es" ? "Selecciona la hora de inicio y de fin." : "Please select both a start and end time.", "error");
					return;
				}
				if (endTime <= startTime) {
					showBanner(language === "es" ? "La hora de fin debe ser después de la hora de inicio." : "The end time needs to be after the start time.", "error");
					return;
				}
			}

			newExclusion = {
				startDate,
				endDate,
				reason: reason || t.reasonDefault,
				blocks: fullDay ? [] : [{ start: startTime, end: endTime }],
			};

			const conflicts = collectBookingConflicts(newExclusion);
			if (conflicts.length > 0) {
				setBookingConflicts(conflicts);
				showBanner(t.conflictsBanner.replace("{count}", conflicts.length), "error");
				return;
			}
			setBookingConflicts([]);

			if (doesOverlap(newExclusion)) {
				showBanner(t.overlapBanner, "error");
				return;
			}
		}

		try {
			setSaving(true);

			/* A 200 from the delete endpoint does not mean the row the user
			   picked is gone. It removes by position in the stored array —
			   REMOVE exclusions[i] — while the week endpoints drop exclusions
			   that have already ended before sending them, so every expired
			   entry shifts our indices by one. DynamoDB happily removes
			   whatever sits at the shifted position and reports success, and
			   this used to show "saved" over a block that was still there.

			   UPDATED_NEW hands back the list as it now stands, so check it
			   rather than the status code. */
			let deleteUnverified = false;

			// deletes first, descending index so earlier removals don't shift later ones
			if (hasDeletes) {
				const indices = [...pendingDeletes].sort((a, b) => b - a);
				const targets = indices.map((i) => signature(exclusions[i]));

				let remaining = null;

				for (const i of indices) {
					// The whole record, so the server can find it rather than
					// count to it. index rides along only for an old server.
					const result = await scheduleBasedRequest.deleteScheduleExclusion(
						selectedScheduleId,
						{ ...exclusions[i], index: i },
						token
					);

					if (Array.isArray(result?.updated?.exclusions)) remaining = result.updated.exclusions;
				}

				// No list back from the server means nothing to check against —
				// don't invent a failure, just don't claim it worked either.
				if (remaining) {
					const survivors = remaining.map(signature);
					deleteUnverified = targets.some((sig) => survivors.includes(sig));
				}
			}

			if (hasAdd) {
				await scheduleBasedRequest.addScheduleExclusion(selectedScheduleId, newExclusion, token);
			}

			if (onSaved) await onSaved();

			if (deleteUnverified) {
				showBanner(t.toast.removeError, "error");
			} else {
				showBanner(t.toast.saved, "success");
				setTimeout(() => {
					clearBanner();
				}, 1500);
			}

			// Reset the "add" form after a successful save so the modal stays open
			// and is ready for the next block, instead of closing automatically.
			if (hasAdd) {
				setStartDate(null);
				setEndDate(null);
				setFullDay(true);
				setStartTime("09:00");
				setEndTime("17:00");
				setReason("");
				setAddDirty(false);
			}
			if (hasDeletes) {
				setPendingDeletes(new Set());
			}
		} catch (err) {
			console.error("Save failed:", err);
			showBanner(t.toast.error, "error");
		} finally {
			setSaving(false);
		}
	}
	// async function saveExclusion() {
	// 	if (!selectedScheduleId) return;

	// 	const t = translations[language].calendar.exclusion;

	// 	const newExclusion = {
	// 		startDate,
	// 		endDate,
	// 		reason: reason || t.reasonDefault,
	// 		blocks: fullDay ? [] : [{ start: startTime, end: endTime }],
	// 	};

	// 	// ⛔ Basic validation
	// 	if (!fullDay) {
	// 		if (!startTime || !endTime) {
	// 			showBanner(language === "es" ? "Selecciona hora de inicio y fin" : "Please select both start and end time", "error");
	// 			return;
	// 		}

	// 		if (endTime <= startTime) {
	// 			showBanner(language === "es" ? "La hora de fin debe ser después de la hora de inicio" : "End time must be after start time", "error");
	// 			return;
	// 		}
	// 	}

	// 	const conflicts = collectBookingConflicts(newExclusion);

	// 	if (conflicts.length > 0) {
	// 		setBookingConflicts(conflicts);

	// 		showBanner(t.conflictsBanner.replace("{count}", conflicts.length), "error");
	// 		return;
	// 	}

	// 	setBookingConflicts([]);

	// 	if (doesOverlap(newExclusion)) {
	// 		showBanner(t.overlapBanner, "error");
	// 		return;
	// 	}

	// 	try {
	// 		setSaving(true);

	// 		await scheduleBasedRequest.addScheduleExclusion(selectedScheduleId, newExclusion, token);

	// 		if (onSaved) await onSaved();

	// 		showBanner(t.toast.saved, "success");

	// 		setTimeout(() => {
	// 			clearBanner();
	// 			onClose();
	// 		}, 800);
	// 	} catch (err) {
	// 		console.error("Error saving exclusion:", err);
	// 		showBanner(t.toast.error, "error");
	// 	} finally {
	// 		setSaving(false);
	// 	}
	// }

	// (the old one-at-a-time delete was replaced by the pending-deletes flow below)

	const toggleDelete = (index) => {
		setPendingDeletes((prev) => {
			const next = new Set(prev);
			next.has(index) ? next.delete(index) : next.add(index);
			return next;
		});
	};

	function formatTimeDisplay(time) {
		if (!time) return "";

		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	return (
		<div className="sf-excl-overlay">
			<div className="sf-excl-modal">
				<div className="sf-excl-header">
					<h3>{translations[language].calendar.exclusion.title}</h3>
					<button className="sf-excl-close" onClick={onClose}>
						✕
					</button>
				</div>

				{/* STAFF PICKER */}
				{staffList.length > 1 && (
					<div className="sf-sdd-div">
						<StaffDropdown
							staffList={staffList}
							selectedStaffId={selectedStaffId || ""}
							onSelect={(id) => {
								const found = staffList.find((s) => s.staffId === id);
								if (found) selectStaff(found);
								setStaffDropdownOpen(false);
							}}
							language={language}
							open={staffDropdownOpen}
							onToggle={() => setStaffDropdownOpen((p) => !p)}
							showAnyOption={false}
						/>
					</div>
				)}

				<div className="sf-excl-tabs">
					<button className={`sf-excl-tab ${activeTab === "add" ? "active" : ""}`} onClick={() => setActiveTab("add")}>
						{language === "es" ? "Bloquear" : "Add block"}
					</button>
					<button className={`sf-excl-tab ${activeTab === "existing" ? "active" : ""}`} onClick={() => setActiveTab("existing")}>
						{language === "es" ? "Existentes" : "Existing"}
						{visibleExclusions.length > 0 && <span className="sf-excl-tab-count">{visibleExclusions.length}</span>}
					</button>
				</div>

				<div className="sf-excl-body">
					{activeTab === "add" && (
						<>
							{/* DATE RANGE — one control, not two stacked fields */}
							<div className="sf-excl-range">
							<div className="sf-excl-box">
								<label>{translations[language].calendar.exclusion.startDate}</label>

								<div
									className={`sf-date-trigger${showStartCalendar ? " is-open" : ""}`}
									onClick={() => setShowStartCalendar((prev) => !prev)}
								>
									<CalendarDays size={15} strokeWidth={2.1} />
									{startDate ? dayjs(startDate).format("MMM D, YYYY") : language === "es" ? "Elegir fecha" : "Select date"}
								</div>

							</div>

							<span className="sf-excl-range-arrow" aria-hidden="true">
								<ArrowRight size={16} strokeWidth={2.2} />
							</span>

							<div className="sf-excl-box">
								<label>{translations[language].calendar.exclusion.endDate}</label>

								<div
									className={`sf-date-trigger${showEndCalendar ? " is-open" : ""}`}
									onClick={() => setShowEndCalendar((prev) => !prev)}
								>
									<CalendarDays size={15} strokeWidth={2.1} />
									{endDate ? dayjs(endDate).format("MMM D, YYYY") : language === "es" ? "Elegir fecha" : "Select date"}
								</div>
							</div>


							</div>

							{/* FULL DAY CHECKBOX */}
							<div className="sf-excl-box">
								<label className="sf-excl-checkbox-label">
									<input
										type="checkbox"
										checked={fullDay}
										onChange={() => {
											setFullDay(!fullDay);
											setAddDirty(true);
										}}
									/>
									&nbsp; {translations[language].calendar.exclusion.fullDay}
								</label>

								{!fullDay && isSingleDay && (
									<div className="sf-excl-time-range">
										<div>
											<label>{translations[language].calendar.exclusion.startTime}</label>
											<select
												value={startTime}
												onChange={(e) => {
													const newStart = e.target.value;

													setStartTime(newStart);

													// find next available slot
													const nextSlot = times.find((t) => t > newStart);

													setEndTime(nextSlot || "");
													setAddDirty(true);
												}}
											>
												<option value="">{language === "es" ? "Inicio" : "Start"}</option>
												{times.map((t) => (
													<option
														key={t}
														value={t}
														disabled={getBlockedRangesForDate(startDate).some((b) => {
															const ts = dayjs(t, "HH:mm");
															const bs = dayjs(b.start, "HH:mm");
															const be = dayjs(b.end, "HH:mm");
															return !ts.isBefore(bs) && ts.isBefore(be);
														})}
													>
														{formatTimeDisplay(t)}
													</option>
												))}
											</select>
										</div>
										<div>
											<label>{translations[language].calendar.exclusion.endTime}</label>
											<select
												value={endTime}
												onChange={(e) => {
													setEndTime(e.target.value);
													setAddDirty(true);
												}}
												disabled={!startTime}
											>
												<option value="">{language === "es" ? "Fin" : "End"}</option>
												{times
													.filter((t) => !startTime || t > startTime)

													.map((t) => (
														<option key={t} value={t} disabled={timeConflicts(startDate, startTime, t)}>
															{formatTimeDisplay(t)}
														</option>
													))}
											</select>
										</div>
									</div>
								)}
							</div>

							{/* REASON */}
							<div className="sf-excl-box">
								<label>{translations[language].calendar.exclusion.reasonLabel}</label>
								<input
									type="text"
									placeholder={translations[language].calendar.exclusion.reasonPlaceholder}
									value={reason}
									onChange={(e) => {
										setReason(e.target.value);
										setAddDirty(true);
									}}
								/>
							</div>
						</>
					)}
					{/* EXISTING EXCLUSIONS */}
					{activeTab === "existing" && visibleExclusions.length === 0 && (
						<div className="sf-excl-empty">
							<CalendarCheck2 size={26} strokeWidth={1.7} />
							<strong>{language === "es" ? "Sin bloqueos" : "No blocks yet"}</strong>
							<span>
								{language === "es"
									? "Los horarios bloqueados aparecerán aquí."
									: "Time you block off will show up here."}
							</span>
						</div>
					)}

					{activeTab === "existing" && visibleExclusions.length > 0 && (
						<div className="sf-excl-section">
							<h4>{translations[language].calendar.exclusion.existing}</h4>

							<div className="sf-excl-list">
								{visibleExclusions.map(({ ex, index: i }) => {
									const isFullDay = ex.blocks?.length === 0 || (ex.blocks?.[0]?.start === "00:00" && ex.blocks?.[0]?.end === "23:59");

									return (
										<div key={i} className={`sf-excl-item ${pendingDeletes.has(i) ? "pending-delete" : ""}`}>
											<div className="sf-excl-item-left">
												<div className="sf-excl-item-date">
													{dayjs(ex.startDate).format("MMM D")} — {dayjs(ex.endDate).format("MMM D")}
												</div>

												<div className="sf-excl-item-range">
													{isFullDay
														? translations[language].calendar.exclusion.fullDayLabel
														: ex.blocks?.map((b) => `${formatTimeDisplay(b.start)} – ${formatTimeDisplay(b.end)}`).join(", ")}
												</div>

												{ex.reason && <div className="sf-excl-item-reason">{ex.reason}</div>}
											</div>

											<button className="sf-excl-delete-btn" onClick={() => toggleDelete(i)}>
												<Trash2 size={18} />
											</button>
										</div>
									);
								})}
							</div>
						</div>
					)}
					{bookingConflicts.length > 0 && (
						<>
							<div className="sf-conflicts-head">
								<AlertTriangle size={13} strokeWidth={2.4} />
								{language === "es" ? "Reservas dentro de este bloqueo" : "Bookings inside this block"}
							</div>

							<div className="sf-conflicts-list">
								{bookingConflicts.map((b) => (
									<div key={b.bookingId || `${b.dateStr}-${b.start}`} className="sf-conflict-item">
										<div className="sf-conflict-left">
											<div className="sf-conflict-client">{b.clientName || (language === "es" ? "Reserva" : "Booking")}</div>

											<div className="sf-conflict-time">
												{dayjs(b.start).format("MMM D")} • {dayjs(b.start).format("h:mm A")} – {dayjs(b.end).format("h:mm A")}
											</div>
										</div>

										{!!onManageBooking && (
											<button
												type="button"
												className="sf-conflict-manage"
												onClick={() =>
													onManageBooking({
														bookingId: b.bookingId,
														dateStr: b.dateStr,
														staffId: b.staffId,
													})
												}
											>
												{language === "es" ? "Gestionar" : "Manage"}
											</button>
										)}
									</div>
								))}
							</div>
						</>
					)}
				</div>

				{/* Date pickers live outside the scrolling body: as an absolute
				    layer over the modal they can't be clipped by its overflow, and
				    opening one no longer pushes the form down. */}
				{activeTab === "add" && (showStartCalendar || showEndCalendar) && (
					<div className="sf-excl-picker-layer">
								{showStartCalendar && (
									<div className="sf-calendar-popover">
										<DayPicker
											mode="single"
											selected={dayjs(startDate).toDate()}
											onSelect={(date) => {
												if (!date) return;

												if (isFullyBlocked(date) || isPast(date)) return;

												const newStart = dayjs(date).format("YYYY-MM-DD");

												setStartDate(newStart);
												setAddDirty(true);

												// 🔥 Auto-fix end date if now invalid
												if (dayjs(newStart).isAfter(dayjs(endDate))) {
													setEndDate(newStart);
												}

												setShowStartCalendar(false);
											}}
											modifiers={{
												past: (date) => isPast(date),
												blocked: (date) => isFullyBlocked(date),
												partial: (date) => isPartiallyBlocked(date),
											}}
											modifiersClassNames={{
												past: "sf-day-past",
												blocked: "sf-day-blocked",
												partial: "sf-day-partial",
											}}
										/>

										{/* the picker dims blocked days and tints partial ones — say so */}
										<div className="sf-excl-legend">
											<span>
												<i className="is-blocked" />
												{language === "es" ? "Bloqueado" : "Blocked"}
											</span>
											<span>
												<i className="is-partial" />
												{language === "es" ? "Parcialmente bloqueado" : "Partly blocked"}
											</span>
										</div>
									</div>
								)}
								{showEndCalendar && (
									<div className="sf-calendar-popover">
										<DayPicker
											mode="single"
											selected={dayjs(endDate).toDate()}
											onSelect={(date) => {
												if (!date) return;

												// Cannot pick before start
												if (dayjs(date).isBefore(dayjs(startDate))) return;

												// Cannot pick blocked
												if (isFullyBlocked(date)) return;

												setEndDate(dayjs(date).format("YYYY-MM-DD"));
												setAddDirty(true);
												setShowEndCalendar(false);
											}}
											modifiers={{
												past: (date) => isPast(date),
												blocked: (date) => isFullyBlocked(date),
												beforeStart: (date) => dayjs(date).isBefore(dayjs(startDate)),
											}}
											modifiersClassNames={{
												past: "sf-day-past",
												blocked: "sf-day-blocked",
												beforeStart: "sf-day-disabled",
											}}
										/>

										<div className="sf-excl-legend">
											<span>
												<i className="is-blocked" />
												{language === "es" ? "Bloqueado" : "Blocked"}
											</span>
										</div>
									</div>
								)}
					</div>
				)}

				<div className="sf-base-footer">
					{banner.message && <BannerMessageFixed text={banner.message} type={banner.type} />}
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ExclusionModal;
